import {
  Button,
  Flex,
  Typography,
  MarkdownPreview,
  Loader,
  AcknowledgeModal,
  Modal,
  ConfirmationModal,
  Spacer,
  SidePanel,
  Alert
} from '@percent/lemonade'
import { TFunction, Trans, useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { CandidateTaskFormProps } from './CandidateTaskForm.types'
import { CandidateTaskFormQuestion } from './candidateTaskFormQuestion/CandidateTaskFormQuestion'
import { MoneyFormField } from './money/MoneyFormField'
import { buildMoneyGroups, moneyRenderDecision } from './money/groupMoneyQuestions'
import {
  CandidateFormQuestion,
  CandidateFormSelectOption
} from '@percent/cause-dashboard/services/grants/grantsService.types'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import {
  useLocale,
  LOCALE_LABELS,
  type SupportedLocale
} from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import { useBrowserTranslation } from '@percent/cause-dashboard/common/hooks/useBrowserTranslation/useBrowserTranslation'
import { useMutation, useQuery, emailRegex, evaluateValidationRules } from '@percent/utility'
import type { CandidateData } from '@percent/utility'
import { useTaskDraftAutosave, DraftValues, TaskDraftSaveStatus } from '@percent/domain/grants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceToNow, type Locale } from 'date-fns'
import { enUS, frCA, zhCN, es, fr, de, ptBR, ja } from 'date-fns/locale'

import styles from './CandidateTaskForm.module.scss'

export const MAX_TEXTAREA_INPUT_LEN = 5000
const LENIENT_URL_EXPRESSION = /^[^@\s]+\.[^@\s]+$/
const JUST_NOW_THRESHOLD_MS = 60_000

const DATE_FNS_LOCALES: Record<SupportedLocale, Locale> = {
  'en-US': enUS,
  'fr-CA': frCA,
  'zh-CN': zhCN,
  'es-ES': es,
  'fr-FR': fr,
  'de-DE': de,
  'pt-BR': ptBR,
  'ja-JP': ja
}

const candidateTaskFormQuestionTypeValidator = (
  question: CandidateFormQuestion,
  t: TFunction,
  candidateData: CandidateData
) => {
  if (question.type === 'select' && question.multiple) {
    const minItems = question.required ? 1 : 0
    return Yup.array().min(minItems, t('grants.grantDetails.candidateTasks.form.validation.required'))
  }

  if (question.type === 'documents') {
    const minFiles = question.required ? 1 : 0
    return Yup.array().min(minFiles, t('grants.grantDetails.candidateTasks.form.validation.required'))
  }

  if (question.type === 'score') {
    const maxValue = question.scoreWeight ? question.maxScore * question.scoreWeight : question.maxScore
    const scoreSchema = Yup.number().min(0).max(maxValue)

    return question.required
      ? scoreSchema.required(t('grants.grantDetails.candidateTasks.form.validation.required'))
      : scoreSchema
  }

  const baseSchema = question.required
    ? Yup.string().required(t('grants.grantDetails.candidateTasks.form.validation.required'))
    : Yup.string()

  let schema: Yup.AnySchema

  switch (question.type) {
    case 'money': {
      // Currency half: an ISO code string, narrowed to the question's allow-list when it carries one.
      if (question.key.endsWith('.currency')) {
        schema = question.allowedCurrencies?.length
          ? baseSchema.oneOf(
              question.allowedCurrencies,
              t('grants.grantDetails.candidateTasks.form.validation.invalidCurrency')
            )
          : baseSchema
        break
      }
      // Amount half: an integer in minor units. Any min/max bounds are expressed as validationRules and
      // enforced by the generic validationRules pass below.
      const amountSchema = Yup.number()
        .typeError(t('grants.grantDetails.candidateTasks.form.validation.invalidNumber'))
        .integer(t('grants.grantDetails.candidateTasks.form.validation.invalidNumber'))
      schema = question.required
        ? amountSchema.required(t('grants.grantDetails.candidateTasks.form.validation.required'))
        : amountSchema
      break
    }
    case 'number': {
      const numberSchema = Yup.number().typeError(t('grants.grantDetails.candidateTasks.form.validation.invalidNumber'))
      schema = question.required
        ? numberSchema.required(t('grants.grantDetails.candidateTasks.form.validation.required'))
        : numberSchema
      break
    }
    case 'textarea': {
      const maxLength = question.maxLength ?? MAX_TEXTAREA_INPUT_LEN
      schema = baseSchema.max(
        maxLength,
        t('grants.grantDetails.candidateTasks.form.validation.maxLength', { maxLength })
      )
      break
    }
    case 'email':
      schema = baseSchema.test('email', t('grants.grantDetails.candidateTasks.form.validation.email'), value => {
        if (!value || value.trim() === '') {
          return !question.required
        }
        return emailRegex.test(value)
      })
      break
    case 'website':
      schema = baseSchema.test('website', t('grants.grantDetails.candidateTasks.form.validation.website'), value => {
        if (!value || value.trim() === '') {
          return !question.required
        }
        return LENIENT_URL_EXPRESSION.test(value)
      })
      break
    default:
      schema = baseSchema
  }

  if (question.validationRules?.length) {
    schema = schema.test('custom-validation', '', function (value) {
      if (value == null || value === '') return true
      const error = evaluateValidationRules(String(value), question.validationRules!, candidateData)
      return error ? this.createError({ message: error }) : true
    })
  }

  return schema
}

function computeVisibilityChanges(
  question: CandidateFormQuestion,
  value: string | number | string[] | File[]
): Record<string, boolean> {
  if (question.type !== 'select' && question.type !== 'decision') {
    return {}
  }

  const options = question.options as CandidateFormSelectOption[]
  const selectedValues = question.type === 'select' && question.multiple ? (value as string[]) : [value as string]
  const visibility: Record<string, boolean> = {}

  for (const option of options) {
    // eslint-disable-next-line no-continue
    if (!option.conditionalQuestionIds?.length) continue

    const isSelected = selectedValues.includes(option.value)

    for (const condId of option.conditionalQuestionIds) {
      visibility[condId] = isSelected || (visibility[condId] ?? false)
    }
  }

  return visibility
}

export function CandidateTaskForm({
  formId,
  programId,
  candidateId,
  taskId,
  onFormSubmitted,
  disabled = false,
  autosaveEnabled = false,
  candidateData,
  assigneeEmail
}: Readonly<CandidateTaskFormProps>) {
  const [modalType, setModalType] = useState<'confirmation' | 'success' | 'error' | null>(null)
  const { t } = useTranslation()

  const { grantsService } = useServices()
  const { locale } = useLocale()
  const { isBrowserTranslationActive } = useBrowserTranslation()

  const { status, lastSavedAt, initialDraft, scheduleSave, clearDraft } = useTaskDraftAutosave<DraftValues>({
    candidateId,
    taskId,
    enabled: autosaveEnabled
  })

  const { data: formData, isLoading } = useQuery(
    ['/forms', formId, programId, locale],
    grantsService.getCandidateForm,
    {
      formId,
      programId,
      candidateId,
      locale
    }
  )

  const [{ isPending: isPostCandidateFormPending }, { apiFunc: postCandidateForm }] = useMutation(
    grantsService.postCandidateForm,
    () => {
      clearDraft()
      setModalType('success')
    },
    () => setModalType('error')
  )

  const [{ isPending: isUploadDocumentsPending }, { mutateAsync: uploadDocuments }] = useMutation(
    grantsService.uploadCandidateDocuments,
    // Do not set success as this is just a part of the submission. If the form fails the documents will be deleted according to the S3 policy
    undefined,
    () => setModalType('error')
  )

  const { candidateForm } = formData || {}

  const questions = useMemo(() => candidateForm?.pages.flatMap(page => page.questions) || [], [candidateForm])

  const [visibleQuestions, setVisibleQuestions] = useState<Record<string, boolean>>({})

  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        questions.reduce((acc, question) => {
          if (question.hidden && !visibleQuestions[question.id]) {
            acc[question.id] = Yup.mixed().notRequired()
          } else {
            acc[question.id] = candidateTaskFormQuestionTypeValidator(question, t, candidateData)
          }

          return acc
        }, {} as Record<string, Yup.AnySchema>)
      ),
    [questions, visibleQuestions, t, candidateData]
  )

  const { handleSubmit, setFieldValue, setFieldTouched, errors, touched, values, setTouched, validateForm } = useFormik(
    {
      initialValues: questions.reduce((acc, question) => {
        const draftValue = initialDraft?.[question.id]

        if (question.type === 'documents' || (question.type === 'select' && question.multiple)) {
          acc[question.id] = Array.isArray(draftValue) ? draftValue : []
        } else if (question.type === 'money' && question.key.endsWith('.currency')) {
          // Seed the currency half so a submission always carries a currency.
          acc[question.id] =
            typeof draftValue === 'string' && draftValue !== '' ? draftValue : question.defaultCurrency ?? 'USD'
        } else {
          acc[question.id] = draftValue ?? ''
        }

        return acc
      }, {} as Record<string, string | number | string[] | File[]>),
      validationSchema,
      onSubmit: async formValues => {
        const visibleEntries = Object.entries(formValues).filter(([questionId]) => {
          const question = questions.find(q => q.id === questionId)

          return !question?.hidden || visibleQuestions[questionId]
        })

        const formAnswers = await Promise.all(
          visibleEntries.map(async ([questionId, answer]) => {
            const isDocumentsQuestion = questions.find(question => question.id === questionId)?.type === 'documents'

            if (isDocumentsQuestion) {
              const formData = new FormData()
              const files = answer as File[]

              if (files.length === 0) {
                return { questionId, answer: [] }
              }

              files.forEach((file, index) => formData.append(`file${index}`, file))
              const { s3Keys } = await uploadDocuments({ candidateId, formData })

              return { questionId, answer: s3Keys }
            }

            if (Array.isArray(answer)) {
              return { questionId, answer: answer as string[] }
            }

            return { questionId, answer: answer.toString() }
          })
        )

        const filteredFormAnswers = formAnswers.filter(({ answer }) => !Array.isArray(answer) || answer.length > 0)

        postCandidateForm({ programId, candidateId, taskId, formAnswers: filteredFormAnswers })
      },
      enableReinitialize: true,
      validateOnMount: true
    }
  )

  useEffect(() => {
    validateForm()
  }, [visibleQuestions, validateForm])

  const hasRestoredVisibility = useRef(false)

  useEffect(() => {
    if (hasRestoredVisibility.current || !initialDraft || questions.length === 0) {
      return
    }

    hasRestoredVisibility.current = true

    const restoredVisibility = questions.reduce((acc, question) => {
      const draftValue = initialDraft[question.id]

      return draftValue === undefined ? acc : { ...acc, ...computeVisibilityChanges(question, draftValue) }
    }, {} as Record<string, boolean>)

    if (Object.keys(restoredVisibility).length > 0) {
      setVisibleQuestions(prev => ({ ...prev, ...restoredVisibility }))
    }
  }, [initialDraft, questions])

  const handleSubmitClick = async () => {
    const validationErrors = await validateForm()

    if (Object.keys(validationErrors).length > 0) {
      setTouched(
        Object.keys(validationErrors).reduce((acc, questionId) => ({ ...acc, [questionId]: true }), {
          ...touched
        })
      )

      return
    }

    setModalType('confirmation')
  }

  const persistDraft = (nextValues: Record<string, string | number | string[] | File[]>) =>
    scheduleSave(
      Object.fromEntries(
        Object.entries(nextValues).filter(
          ([questionId]) => questions.find(q => q.id === questionId)?.type !== 'documents'
        )
      ) as DraftValues
    )

  const renderSavedLabel = useCallback(
    (savedAt: number) =>
      Date.now() - savedAt < JUST_NOW_THRESHOLD_MS
        ? t('grants.grantDetails.candidateTasks.autoSaveStatus.savedJustNow')
        : t('grants.grantDetails.candidateTasks.autoSaveStatus.saved', {
            relativeTime: formatDistanceToNow(savedAt, { addSuffix: true, locale: DATE_FNS_LOCALES[locale] })
          }),
    [t, locale]
  )

  const handleChange = (questionId: string, value: string | number | string[] | File[]) => {
    const question = questions.find(q => q.id === questionId)
    const visibilityChanges = question ? computeVisibilityChanges(question, value) : {}

    if (Object.keys(visibilityChanges).length > 0) {
      setVisibleQuestions(prev => ({ ...prev, ...visibilityChanges }))

      const nextTouched = { ...touched, [questionId]: true } as Record<string, boolean>

      for (const [condId, visible] of Object.entries(visibilityChanges)) {
        if (!visible) {
          const condQuestion = questions.find(q => q.id === condId)

          if (condQuestion) {
            setFieldValue(condId, condQuestion.type === 'documents' ? [] : '')
            nextTouched[condId] = false
          }
        }
      }

      setTouched(nextTouched)
    }

    setFieldValue(questionId, value)
    persistDraft({ ...values, [questionId]: value })
  }

  const closeModal = () => setModalType(null)

  const successModal = modalType === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.grantDetails.candidateTasks.form.confirmationModal.success.title')}
      buttonText={t('button.close')}
      handleClose={() => {
        onFormSubmitted()
        closeModal()
      }}
      viewTestId="task-form-success-modal"
      buttonTestId="close-task-form-success-modal-button"
    />
  )

  const errorModal = modalType === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.grantDetails.candidateTasks.form.confirmationModal.error.title')}
      description={t('grants.grantDetails.candidateTasks.form.confirmationModal.error.description')}
      buttonText={t('button.done')}
      handleClose={closeModal}
      viewTestId="task-form-error-modal"
      buttonTestId="close-task-form-error-modal-button"
    />
  )

  if (isLoading || !candidateForm) {
    return <Loader />
  }

  return (
    <>
      <Flex direction="column">
        <Flex direction="column" gap={16} mt={8} mb={16}>
          {disabled && assigneeEmail && (
            <Alert variant="info" title={t('grants.grantDetails.candidateTasks.alert.title', { assigneeEmail })}>
              {t('grants.grantDetails.candidateTasks.alert.description')}
            </Alert>
          )}
          <Typography variant="h4">{candidateForm.title}</Typography>
          <MarkdownPreview content={candidateForm.description} className={styles.description} />
        </Flex>

        {candidateForm.pages
          ?.sort((a, b) => a.order - b.order)
          .map((page, index) => {
            const moneyGroups = buildMoneyGroups(page.questions)

            return (
              <Flex
                key={page.id}
                className={styles.pageWrapper}
                data-is-last={index === candidateForm.pages.length - 1}
              >
                <Flex direction="column" gap={8}>
                  {page.title !== candidateForm.title && <Typography variant="h4">{page.title}</Typography>}
                  {page.description && <MarkdownPreview content={page.description} className={styles.description} />}
                </Flex>

                {page.questions
                  ?.filter(question => !question.hidden || visibleQuestions[question.id])
                  ?.sort((a, b) => a.order - b.order)
                  .map(question => {
                    const money = moneyRenderDecision(question, moneyGroups)

                    if (money) {
                      if (!money.render) return null

                      return (
                        <MoneyFormField
                          key={question.id}
                          amountQuestion={money.group.amount}
                          currencyQuestion={money.group.currency}
                          currencyOnly={money.currencyOnly}
                          values={values}
                          errors={errors as Record<string, string | undefined>}
                          touched={touched as Record<string, boolean | undefined>}
                          onChange={handleChange}
                          locale={locale}
                          disabled={disabled}
                        />
                      )
                    }

                    return (
                      <CandidateTaskFormQuestion
                        key={question.id}
                        question={question}
                        handleChange={handleChange}
                        handleBlur={(fieldName: string) => setFieldTouched(fieldName, true)}
                        value={values?.[question.id]}
                        error={errors?.[question.id]}
                        touched={touched?.[question.id]}
                        disabled={disabled}
                      />
                    )
                  })}
              </Flex>
            )
          })}

        <SidePanel.Footer justify="space-between" align="center">
          <span>
            {autosaveEnabled && (
              <TaskDraftSaveStatus
                status={status}
                lastSavedAt={lastSavedAt}
                savingLabel={t('grants.grantDetails.candidateTasks.autoSaveStatus.saving')}
                errorLabel={t('grants.grantDetails.candidateTasks.autoSaveStatus.error')}
                renderSavedLabel={renderSavedLabel}
              />
            )}
          </span>
          <Button
            onClick={handleSubmitClick}
            disabled={disabled}
            loading={isPostCandidateFormPending}
            data-testid="submit-task-form-button"
          >
            {t('grants.grantDetails.candidateTasks.submit')}
          </Button>
        </SidePanel.Footer>
      </Flex>

      {modalType && (
        <Modal open={!!modalType} onClose={closeModal}>
          {successModal || errorModal || (
            <ConfirmationModal
              title={t('grants.grantDetails.candidateTasks.form.confirmationModal.title')}
              loading={isUploadDocumentsPending || isPostCandidateFormPending}
              primaryButtonText={t('grants.grantDetails.candidateTasks.form.confirmationModal.submit')}
              primaryBtnTestId="submit-task-form-confirmation-button"
              secondaryButtonText={t('button.cancel')}
              handleSubmit={handleSubmit}
              handleClose={closeModal}
            >
              <Trans
                i18nKey="grants.grantDetails.candidateTasks.form.confirmationModal.description"
                components={{ br: <Spacer axis="vertical" size={4} /> }}
              />
              {isBrowserTranslationActive && (
                <Flex direction="column" mt={16} data-testid="browser-translation-warning">
                  <Alert
                    variant="warning"
                    title={t('grants.grantDetails.candidateTasks.form.confirmationModal.browserTranslation.title', {
                      language: LOCALE_LABELS[locale]
                    })}
                  >
                    {t('grants.grantDetails.candidateTasks.form.confirmationModal.browserTranslation.description', {
                      language: LOCALE_LABELS[locale]
                    })}
                  </Alert>
                </Flex>
              )}
            </ConfirmationModal>
          )}
        </Modal>
      )}
    </>
  )
}
